/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-01-11 07:47:45
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field } from 'redux-form';
import { Stack, TextStyle, SkeletonBodyText, Tooltip, Icon } from '@shopify/polaris';
import { QuestionMarkMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { getFormValues } from 'redux-form';

import NoSearchResult from 'src/components/views/tools/translation/tabs/search-tab/no-search-result';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { hasTag } from 'src/helpers/form-validation-helper';
import { prettyText } from 'src/helpers';
import { RECOMMENDATION_TAB } from 'src/constants/translation';

type Props = {
  locale: string,
  primaryLocale: string,
  translationValues: Object,
  translationFields: Object,
  inputSearchText: string
};

type ComposedProps = Props & WithI18nProps;

type State = {
  searching: boolean,
  groupItems: Object,
  disabledField: boolean
};

class SearchTab extends Component<ComposedProps, State> {
  timeoutSearch = undefined;

  state = {
    searching: false,
    groupItems: {},
    disabledField: false
  };

  componentDidMount() {
    const { inputSearchText } = this.props;

    if (inputSearchText) {
      this.setState({ searching: true });
      this.searchValue(inputSearchText);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { inputSearchText: prevInputSearchText } = this.props;
    const { inputSearchText } = nextProps;

    if (inputSearchText !== prevInputSearchText) {
      clearTimeout(this.timeoutSearch);
      this.setState({ searching: true });
      // Set time out to search
      this.timeoutSearch = setTimeout(() => {
        this.searchValue(inputSearchText);
      }, 500);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutSearch);
  }

  render() {
    const { groupItems, searching } = this.state;
    return searching ? (
      <SkeletonBodyText />
    ) : _.isEmpty(groupItems) ? (
      <NoSearchResult />
    ) : (
      <div style={{ marginTop: '-2.5rem' }}>
        {_.map(groupItems, (items, key) => this.renderGroupFields(items, key))}
      </div>
    );
  }

  renderGroupFields = (items: Object = [], key: string) => {
    const { i18n, locale, primaryLocale } = this.props;
    const disabledRecommendationField = key === RECOMMENDATION_TAB && locale === primaryLocale;
    const displayItems = items.reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / 2);
      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = []; // start a new chunk
      }
      resultArray[chunkIndex].push(item);
      return resultArray;
    }, []);

    const translationTitle = (
      <TextStyle variation="strong">
        {i18n.translate(`AdminHTML.Translation.${key}.title`)}
      </TextStyle>
    );

    return (
      <div key={key} style={{ marginTop: '2.5rem' }}>
        {disabledRecommendationField ? (
          <span className="search-result--withIcon">
            {translationTitle}
            <Tooltip
              content={i18n.translate(`AdminHTML.Translation.${key}.disabledTooltip`)}
              preferredPosition="mostSpace">
              <Icon source={QuestionMarkMajor} color="base" />
            </Tooltip>
          </span>
        ) : (
          translationTitle
        )}
        <div style={{ marginTop: '8px' }}>
          {_.map(displayItems, (displayItem, index) => (
            <div key={index} className={index === 0 ? 'first' : ''}>
              <Stack distribution="fill" wrap={true} alignment="trailing" key={index}>
                <div>
                  {!_.isEmpty(displayItem[0])
                    ? this.renderField(displayItem[0], disabledRecommendationField)
                    : ''}
                </div>
                <div>
                  {!_.isEmpty(displayItem[1])
                    ? this.renderField(displayItem[1], disabledRecommendationField)
                    : ''}
                </div>
              </Stack>
            </div>
          ))}
        </div>
      </div>
    );
  };

  renderField = (item: Object, disabled: boolean) => {
    const { locale } = this.props;
    return (
      <div>
        <Field
          name={`${locale}.${item.name}`}
          component={RenderTextField}
          label={item.label}
          placeholder={item.placeholder}
          validate={[hasTag]}
          anotherProps={{ disabled }}
        />
      </div>
    );
  };

  searchValue = (searchText: string) => {
    const { translationValues, translationFields, locale, recommendation } = this.props;
    const activeRecommendation = _.get(recommendation, 'status', 'inactive');
    const enableRecommendation = _.get(recommendation, 'settings.enable', false);

    const prettySearchText = prettyText(searchText).toLowerCase();
    let groupItems = {};

    // Convert recommendation fields from object to array
    const flatTranslationFields = { ...translationFields };
    if (flatTranslationFields[RECOMMENDATION_TAB]) {
      flatTranslationFields[RECOMMENDATION_TAB] = Object.keys(
        flatTranslationFields[RECOMMENDATION_TAB]
      ).reduce((r, k) => r.concat(flatTranslationFields[RECOMMENDATION_TAB][k]), []);
    }

    // Search on label, placeholder from translationFields and value from translationValues
    _.map(flatTranslationFields, (tabFields, key) => {
      const items = [];
      _.map(tabFields, field => {
        const { name, label = '', placeholder = '' } = field;
        if (key === RECOMMENDATION_TAB) {
          if (
            (name.toLowerCase().includes(prettySearchText) ||
              label.split('(')[0].toLowerCase().includes(prettySearchText)) &&
            activeRecommendation === 'active' &&
            enableRecommendation
          ) {
            items.push(field);
          }
        } else {
          if (
            label.toLowerCase().includes(prettySearchText) ||
            placeholder.toLowerCase().includes(prettySearchText)
          ) {
            items.push(field);
          } else {
            const value = _.get(translationValues, `${locale}.${name}`, '');
            if (value.toLowerCase().includes(prettySearchText)) {
              items.push(field);
            }
          }
        }
      });
      if (!_.isEmpty(items)) {
        groupItems[key] = items;
      }
    });

    this.setState({ groupItems, searching: false });
  };
}

const mapStateToProps = (state, props) => {
  // Get translation values
  const translationValues = getFormValues('TranslationForm')(state);

  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const recommendation = _.get(tenantApp, 'settings.recommendation', {});

  return {
    translationValues,
    recommendation
  };
};

export default withI18n()(connect(mapStateToProps, {})(SearchTab));
